import {
  Alert,
  Button,
  Card,
  Col,
  Empty,
  Input,
  message,
  Popconfirm,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import dayjs from 'dayjs';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useRef, useState } from 'react';

import ClubScheduleSection from '../ClubSchedule/ClubScheduleSection';
import ClubTypeIcon from './ClubTypeIcon';
import MembershipDrawer from './MembershipDrawer';
import ParentLink from './ParentLink';
import PayAheadDrawer from './PayAheadDrawer';
import SplitInvoiceDrawer from './SplitInvoiceDrawer';
import type { ClubOption, MemberRow, OutstandingRow } from './types';
import { formatMNT, formatPeriod, queryErrorMessage } from './types';

const { Text, Title } = Typography;

// Who the student is — the first columns of both lists.
const studentColumns = <T extends MemberRow | OutstandingRow>(): ColumnsType<T> => [
  { title: 'Сурагч', dataIndex: 'student_name' },
  { title: 'Анги', dataIndex: 'class_name', render: (v) => v ?? '—' },
  {
    title: 'Эцэг эх',
    dataIndex: 'parent_name',
    render: (v, row) => <ParentLink name={v} parent={row.parent} />,
  },
  {
    title: 'Ваучер',
    dataIndex: 'has_voucher',
    render: (v) => (v ? <Tag color="green">Ваучертай</Tag> : '—'),
  },
];

const memberColumns: ColumnsType<MemberRow> = [
  ...studentColumns<MemberRow>(),
  {
    title: 'Элссэн огноо',
    dataIndex: 'joined_date',
    render: (v) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
  },
  {
    title: 'Төлбөр',
    dataIndex: 'outstanding_amount',
    render: (v: number) =>
      v > 0 ? <Tag color="orange">Төлбөрийн үлдэгдэлтэй</Tag> : <Tag color="green">Төлөгдсөн</Tag>,
  },
];

// 15 хоногоос удаан төлөгдөөгүй бол улаанаар тодруулна.
const STALE_OUTSTANDING_DAYS = 15;

// "2026-09-12 (15 хоногийн өмнө)" — хэр удаан төлөгдөөгүй байгааг харуулна.
const daysSince = (v: string) => dayjs().startOf('day').diff(dayjs(v).startOf('day'), 'day');
// An invoice unpaid for longer than STALE_OUTSTANDING_DAYS is overdue.
const isOverdue = (row: OutstandingRow) =>
  !!row.created_date && daysSince(row.created_date) > STALE_OUTSTANDING_DAYS;

const renderCreated = (v: string | null) => {
  if (!v) return '—';
  const days = daysSince(v);
  const ago = days === 0 ? 'өнөөдөр' : `${days} хоногийн өмнө`;
  const label = `${dayjs(v).format('YYYY-MM-DD')} (${ago})`;
  return days > STALE_OUTSTANDING_DAYS ? <Text type="danger">{label}</Text> : label;
};

const outstandingColumns: ColumnsType<OutstandingRow> = [
  ...studentColumns<OutstandingRow>(),
  {
    title: 'Хугацаа',
    key: 'period',
    render: (_, row) =>
      row.period_start ? (
        <Text style={{ fontSize: 12 }}>{formatPeriod(row, { withSessions: false })}</Text>
      ) : (
        <Text type="secondary" style={{ fontSize: 12 }}>
          Хуучин нэхэмжлэл
        </Text>
      ),
  },
  { title: 'Нэхэмжлэл үүссэн', dataIndex: 'created_date', render: renderCreated },
];

const ClubRegistration = () => {
  const { level, runQuery } = useScreen();
  // Шинэ гишүүнчлэл / цуцлах / төлөх нь edit түвшний Query-гаар бичдэг тул
  // view эрхтэй хэрэглэгчид товчийг харуулахгүй (сервер ч мөн татгалзана).
  const canEdit = level === 'edit';
  const [clubs, setClubs] = useState<ClubOption[]>([]);
  const [clubsLoading, setClubsLoading] = useState(false);
  const [clubsError, setClubsError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const [selectedClubId, setSelectedClubId] = useState<number>();

  const [members, setMembers] = useState<MemberRow[]>([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [outstanding, setOutstanding] = useState<OutstandingRow[]>([]);
  const [outstandingLoading, setOutstandingLoading] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'join' | 'cancel' | null>(null);
  const [payAheadMember, setPayAheadMember] = useState<MemberRow | null>(null);
  const [splitInvoice, setSplitInvoice] = useState<OutstandingRow | null>(null);

  // Дугуйлан хурдан дараалан солиход хоцорсон хариу эрт ирсэн хариуг дарж
  // бичихээс сэргийлнэ.
  const requestIdRef = useRef(0);

  const loadClubs = useCallback(() => {
    setClubsLoading(true);
    runQuery<ClubOption>('club_options')
      .then((items) => {
        setClubs(items);
        if (items.length === 0) {
          setClubsError('Дугуйлан бүртгэгдээгүй байна.');
        } else {
          setSelectedClubId((current) => current ?? items[0].id);
        }
      })
      .catch((e) => setClubsError(queryErrorMessage(e)))
      .finally(() => setClubsLoading(false));
  }, [runQuery]);

  useEffect(() => {
    loadClubs();
  }, [loadClubs]);

  const loadStudents = useCallback(
    (clubId: number | undefined) => {
      if (!clubId) {
        setMembers([]);
        setOutstanding([]);
        return;
      }

      const requestId = ++requestIdRef.current;
      const load = <Row,>(
        queryKey: string,
        setRows: (rows: Row[]) => void,
        setLoading: (loading: boolean) => void,
      ) => {
        setLoading(true);
        runQuery<Row>(queryKey, { clubId })
          .then((rows) => {
            if (requestId === requestIdRef.current) setRows(rows);
          })
          .catch((e) => {
            if (requestId === requestIdRef.current) message.error(queryErrorMessage(e));
          })
          .finally(() => {
            if (requestId === requestIdRef.current) setLoading(false);
          });
      };

      load<MemberRow>('club_members', setMembers, setMembersLoading);
      load<OutstandingRow>('club_outstanding_invoices', setOutstanding, setOutstandingLoading);
    },
    [runQuery],
  );

  useEffect(() => {
    loadStudents(selectedClubId);
  }, [selectedClubId, loadStudents]);

  // Outstanding payment-ийг картын үлдэгдлээс төлнө (edit).
  const [payingId, setPayingId] = useState<number | null>(null);
  const pay = (row: OutstandingRow) => {
    setPayingId(row.id);
    runQuery('club_pay', { invoiceId: row.id })
      .then(() => {
        message.success('Төлбөр төлөгдлөө.');
        loadStudents(selectedClubId);
        loadClubs();
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setPayingId(null));
  };

  const outstandingTableColumns: ColumnsType<OutstandingRow> = canEdit
    ? [
        ...outstandingColumns,
        {
          key: 'actions',
          align: 'right',
          render: (_, row) => {
            // Overdraft зөвшөөрөхгүй — үлдэгдэл хүрэлцэхгүй бол төлөх боломжгүй.
            const short = row.card_balance < row.amount;
            // One from before monthly billing (no Club month) may be split by Club month.
            const splittable = row.months_count == null && row.amount > 0;
            return (
              <Space size={8}>
                {splittable && (
                  <Button size="small" onClick={() => setSplitInvoice(row)}>
                    Сараар хуваах
                  </Button>
                )}
                <Tooltip title={short ? 'Картын үлдэгдэл хүрэлцэхгүй' : undefined}>
                  <Popconfirm
                    title="Төлбөр төлөх үү?"
                    description={`${formatMNT(row.amount)}-г ${row.student_name}-ийн картын үлдэгдлээс хасна.`}
                    okText="Төлөх"
                    cancelText="Болих"
                    onConfirm={() => pay(row)}
                    disabled={short}
                  >
                    <Button
                      size="small"
                      type="primary"
                      disabled={short}
                      loading={payingId === row.id}
                    >
                      Төлөх
                    </Button>
                  </Popconfirm>
                </Tooltip>
              </Space>
            );
          },
        },
      ]
    : outstandingColumns;

  const memberTableColumns: ColumnsType<MemberRow> = canEdit
    ? [
        ...memberColumns,
        {
          key: 'actions',
          align: 'right',
          render: (_, row) => (
            <Button size="small" onClick={() => setPayAheadMember(row)}>
              Урьдчилж төлөх
            </Button>
          ),
        },
      ]
    : memberColumns;

  const selectedClub = clubs.find((c) => c.id === selectedClubId);
  const overdueCount = outstanding.filter(isOverdue).length;
  const filteredClubs = search.trim()
    ? clubs.filter((c) => c.name.toLowerCase().includes(search.trim().toLowerCase()))
    : clubs;

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      {!clubsLoading && clubsError && (
        <Alert type="warning" showIcon message={clubsError} style={{ marginBottom: 16 }} />
      )}

      {clubsLoading && <Spin />}

      {!clubsLoading && !clubsError && (
        <Row gutter={16}>
          <Col xs={24} lg={7}>
            <Card size="small" styles={{ body: { padding: 12 } }}>
              <Input.Search
                placeholder="Дугуйлан хайх..."
                allowClear
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ marginBottom: 12 }}
              />
              <div style={{ maxHeight: 'calc(100vh - 260px)', overflowY: 'auto' }}>
                {filteredClubs.length === 0 && <Empty description="Дугуйлан алга" />}
                {filteredClubs.map((c) => {
                  const isSelected = c.id === selectedClubId;
                  return (
                    <Card
                      key={c.id}
                      size="small"
                      hoverable
                      onClick={() => setSelectedClubId(c.id)}
                      style={{
                        marginBottom: 8,
                        cursor: 'pointer',
                        borderColor: isSelected ? '#1677ff' : undefined,
                        boxShadow: isSelected ? '0 0 0 1px #1677ff' : undefined,
                      }}
                      styles={{ body: { padding: 10 } }}
                    >
                      <div className="flex gap-3">
                        <ClubTypeIcon icon={c.club_type_icon} name={c.club_type_name} />
                        <div className="min-w-0 flex-1">
                          <Text strong>{c.name}</Text>
                          <div>
                            <Text type="secondary" style={{ fontSize: 12 }}>
                              {[c.club_type_name, c.teacher_name].filter(Boolean).join(' · ') ||
                                '—'}
                            </Text>
                          </div>
                          <div style={{ marginTop: 6, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            <Tag color="green">Гишүүд: {c.member_count}</Tag>
                            {c.unpaid_student_count > 0 && (
                              <Tag color="red">Төлөгдөөгүй: {c.unpaid_student_count}</Tag>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={17}>
            {!selectedClub && (
              <Card>
                <Empty description="Дугуйлан сонгоно уу" />
              </Card>
            )}

            {selectedClub && (
              <>
                <Card style={{ marginBottom: 16 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <div className="flex min-w-0 gap-4">
                      <ClubTypeIcon
                        icon={selectedClub.club_type_icon}
                        name={selectedClub.club_type_name}
                        size={52}
                      />
                      <div className="min-w-0">
                        <Title level={4} style={{ marginBottom: 4 }}>
                          {selectedClub.name}
                        </Title>
                        <Text type="secondary">
                          {[
                            selectedClub.week_days.join(', '),
                            selectedClub.timesheet,
                            selectedClub.available_spot != null
                              ? `${selectedClub.available_spot} хүүхэд`
                              : null,
                          ]
                            .filter(Boolean)
                            .join(' · ')}
                        </Text>
                        <div>
                          {selectedClub.start_date ? (
                            <Text type="secondary">
                              Эхлэх огноо: {dayjs(selectedClub.start_date).format('YYYY-MM-DD')}
                            </Text>
                          ) : (
                            // join() refuses a club without a start date.
                            <Text type="warning">Эхлэх огноо тохируулаагүй</Text>
                          )}
                        </div>
                      </div>
                    </div>
                    {canEdit && (
                      <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                        <Button onClick={() => setDrawerMode('join')}>Шинэ гишүүнчлэл</Button>
                        <Button danger onClick={() => setDrawerMode('cancel')}>
                          Гишүүнчлэл цуцлах
                        </Button>
                      </div>
                    )}
                  </div>
                </Card>

                <Row gutter={16} style={{ marginBottom: 16 }}>
                  {[
                    { title: 'Сурагчид', value: selectedClub.member_count, alert: false },
                    {
                      title: 'Төлөгдөөгүй сурагч',
                      value: selectedClub.unpaid_student_count,
                      alert: selectedClub.unpaid_student_count > 0,
                    },
                    {
                      title: 'Хугацаа хэтэрсэн төлбөр',
                      value: overdueCount,
                      alert: overdueCount > 0,
                      loading: outstandingLoading,
                    },
                  ].map(({ title, value, alert, loading }) => (
                    <Col key={title} xs={24} sm={8} style={{ marginBottom: 8 }}>
                      <Card>
                        <Statistic
                          title={title}
                          value={value}
                          loading={loading}
                          valueStyle={alert ? { color: '#cf1322' } : undefined}
                        />
                      </Card>
                    </Col>
                  ))}
                </Row>

                <ClubScheduleSection
                  key={selectedClub.id}
                  club={{
                    id: selectedClub.id,
                    start_date: selectedClub.start_date,
                    week_days: selectedClub.week_day_numbers,
                  }}
                  canEdit={canEdit}
                />

                <Card style={{ marginBottom: 16 }}>
                  <Title level={5} style={{ marginBottom: 12 }}>
                    Төлөгдөөгүй төлбөр ({outstanding.length})
                  </Title>
                  {outstandingLoading && <Spin />}
                  {!outstandingLoading && outstanding.length === 0 && (
                    <Empty description="Төлөгдөөгүй төлбөр алга" />
                  )}
                  {!outstandingLoading && outstanding.length > 0 && (
                    <Table
                      rowKey="id"
                      columns={outstandingTableColumns}
                      dataSource={outstanding}
                      pagination={false}
                    />
                  )}
                </Card>

                <Card>
                  <Title level={5} style={{ marginBottom: 12 }}>
                    Гишүүд ({members.length})
                  </Title>
                  {membersLoading && <Spin />}
                  {!membersLoading && members.length === 0 && <Empty description="Гишүүн алга" />}
                  {!membersLoading && members.length > 0 && (
                    <Table
                      rowKey="id"
                      columns={memberTableColumns}
                      dataSource={members}
                      pagination={false}
                    />
                  )}
                </Card>
              </>
            )}
          </Col>
        </Row>
      )}

      {canEdit && drawerMode && selectedClub && (
        <MembershipDrawer
          mode={drawerMode}
          club={selectedClub}
          members={members}
          onClose={() => setDrawerMode(null)}
          onDone={() => {
            setDrawerMode(null);
            loadStudents(selectedClubId);
            loadClubs();
          }}
        />
      )}

      {canEdit && splitInvoice && (
        <SplitInvoiceDrawer
          invoice={splitInvoice}
          onClose={() => setSplitInvoice(null)}
          onDone={() => {
            setSplitInvoice(null);
            loadStudents(selectedClubId);
            loadClubs();
          }}
        />
      )}

      {canEdit && payAheadMember && (
        <PayAheadDrawer
          member={payAheadMember}
          onClose={() => setPayAheadMember(null)}
          onDone={() => {
            setPayAheadMember(null);
            loadStudents(selectedClubId);
            loadClubs();
          }}
        />
      )}
    </LayoutWithMenu>
  );
};

export default ClubRegistration;
